import React from 'react';
import { RhythmPatternStrip } from './RhythmPatternStrip';
import {
  VexStaffFragment,
  VexRhythmGlyph,
  humanizeMusicText,
  tokenToLabel,
  normalizeTokenArray,
  BeatCellWorkshopGrid,
  buildBarBeatStructure,
  BeatCellStrip,
  VexPhraseScore,
  buildPhraseScoreBars,
  phraseScoreCaption,
  SymbolAnalysisStaff,
  FigureLegendPanel,
} from '@interactive-room';
import type { MusicalToken, RhythmicCell, ScoreRenderEvent, FigureLegendEntry } from '@interactive-room';

function resolveScoreEvents(
  payload: Record<string, unknown>,
  barNum?: number,
): ScoreRenderEvent[] | undefined {
  const byBar = payload.scoreEventsByBar as Record<string | number, ScoreRenderEvent[]> | undefined;
  if (barNum != null && byBar) {
    const fromBar = byBar[barNum] ?? byBar[String(barNum)];
    if (Array.isArray(fromBar) && fromBar.length) return fromBar;
  }
  if (Array.isArray(payload.scoreEvents) && payload.scoreEvents.length) {
    return payload.scoreEvents as ScoreRenderEvent[];
  }
  return undefined;
}

function resolveBlankIndices(payload: Record<string, unknown>, tokens: MusicalToken[]): number[] {
  const blankIndices = Array.isArray(payload.blankIndices)
    ? (payload.blankIndices as number[]).filter((i) => i >= 0 && i < tokens.length)
    : [];
  if (blankIndices.length) return blankIndices;
  const taskKind = String(payload.taskKind || 'complete');
  if (taskKind === 'transcribe' || taskKind === 'practice') {
    return tokens.map((_, i) => i);
  }
  if (taskKind === 'syllables') {
    return tokens.map((_, i) => i);
  }
  const missingIndex = Number(payload.missingIndex);
  if (Number.isFinite(missingIndex) && missingIndex >= 0 && missingIndex < tokens.length) {
    return [missingIndex];
  }
  return tokens.length ? [tokens.length - 1] : [];
}

function resolveBeatCells(payload: Record<string, unknown>, tokens: MusicalToken[]): RhythmicCell[] {
  if (Array.isArray(payload.beatCells) && payload.beatCells.length) {
    return payload.beatCells as RhythmicCell[];
  }
  const ts = String(payload.timeSignature || payload.meter || '4/4');
  return buildBarBeatStructure(tokens, ts as never).beatCells;
}

function resolveBlankIndicesForBeats(payload: Record<string, unknown>, beatCount: number): number[] {
  const blankIndices = Array.isArray(payload.blankIndices)
    ? (payload.blankIndices as number[]).filter((i) => i >= 0 && i < beatCount)
    : [];
  if (blankIndices.length) return blankIndices;
  const taskKind = String(payload.taskKind || 'complete');
  if (taskKind === 'transcribe' || taskKind === 'practice' || taskKind === 'syllables') {
    return Array.from({ length: beatCount }, (_, i) => i);
  }
  const missingIndex = Number(payload.missingIndex);
  if (Number.isFinite(missingIndex) && missingIndex >= 0 && missingIndex < beatCount) {
    return [missingIndex];
  }
  return beatCount ? [beatCount - 1] : [];
}

function PrintBeatCellWorkshopSlots({
  payload,
  beatCells,
  method,
}: {
  payload: Record<string, unknown>;
  beatCells: RhythmicCell[];
  method: 'tradicional' | 'takadimi' | 'kodaly';
}) {
  const tokens = normalizeTokenArray(
    (payload.originalBarTokens as string[] | undefined) ??
      (payload.patternTokens as string[] | undefined),
  ) as MusicalToken[];
  const blankIndices = resolveBlankIndicesForBeats(payload, beatCells.length);

  return (
    <BeatCellWorkshopGrid
      beatCells={beatCells}
      timeSignature={(payload.timeSignature as never) ?? '4/4'}
      readingMethod={method}
      taskKind={String(payload.taskKind || 'complete') as never}
      blankIndices={blankIndices}
      missingIndex={Number(payload.missingIndex) || undefined}
      dottedIndices={
        Array.isArray(payload.rhythmDots) ? (payload.rhythmDots as number[]) : []
      }
      targetBarNumber={Number(payload.targetBarNumber ?? payload.barIndex) || undefined}
      graphicMode={payload.graphicMode as never}
      hidePattern={payload.hidePattern === true}
      patternTokens={tokens}
      values={{}}
      disabled
      onSlotChange={() => {}}
      printMode
    />
  );
}

function PrintRhythmWorkshopSlots({
  payload,
  tokens,
  method,
  beatCells,
}: {
  payload: Record<string, unknown>;
  tokens: MusicalToken[];
  method: 'tradicional' | 'takadimi' | 'kodaly';
  beatCells?: RhythmicCell[];
}) {
  const taskKind = String(payload.taskKind || 'complete');
  const scoreEvents = resolveScoreEvents(payload, Number(payload.targetBarNumber ?? payload.barIndex) || undefined);
  const slotCount = beatCells?.length ?? tokens.length;
  const blankIndices = resolveBlankIndicesForBeats(payload, slotCount);
  const isSyllables = taskKind === 'syllables';
  const isTranscribe = taskKind === 'transcribe' || taskKind === 'practice';
  const hidePattern = payload.hidePattern === true;
  const showStaff = !hidePattern && (tokens.length > 0 || Boolean(scoreEvents?.length));

  return (
    <>
      {showStaff && (
        <RhythmPatternStrip
          rhythmPattern={payload.rhythmPattern as never}
          patternTokens={tokens}
          beatCells={beatCells}
          scoreEvents={scoreEvents}
          timeSignature={payload.timeSignature as never}
          meter={payload.meter as string | undefined}
          targetBarNumber={Number(payload.targetBarNumber ?? payload.barIndex) || undefined}
          dottedIndices={
            Array.isArray(payload.rhythmDots)
              ? (payload.rhythmDots as number[])
              : Array.isArray(payload.dottedIndices)
                ? (payload.dottedIndices as number[])
                : []
          }
          ties={
            Array.isArray((payload.modifiers as { ties?: [number, number][] } | undefined)?.ties)
              ? (payload.modifiers as { ties: [number, number][] }).ties
              : []
          }
          keySignature={(payload.modifiers as { keySignature?: string } | undefined)?.keySignature}
          rhythmMethod={payload.rhythmMethod as never}
          graphicMode={payload.graphicMode as never}
          slotMode="missing"
          printMode
          showClef
        />
      )}
      {hidePattern && isTranscribe && (
        <p className="print-hint">Escucha al docente, percute en metalófono y escribe en los casilleros.</p>
      )}
      {slotCount > 0 && (
        <div className="print-rhythm-workshop__grid" aria-label="Casilleros de respuesta">
          {Array.from({ length: slotCount }).map((_, index) => {
            const isBlank = blankIndices.includes(index);
            if (!isBlank && taskKind === 'complete') return null;
            return (
              <div key={index} className="print-rhythm-workshop__cell print-rhythm-workshop__cell--write">
                <span className="print-rhythm-workshop__num">{index + 1}</span>
                <div className="print-rhythm-workshop__blank" aria-hidden />
                {isSyllables && (
                  <span className="print-rhythm-workshop__hint">
                    {isBlank ? 'silaba' : tokenToLabel(tokens[index] ?? 'Q', method)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function perBarSliceIndices(globalIndices: number[], tokenStart: number, tokenEnd: number): number[] {
  return globalIndices.filter((i) => i >= tokenStart && i < tokenEnd).map((i) => i - tokenStart);
}

function perBarTiePairs(globalTies: [number, number][], tokenStart: number, tokenEnd: number): [number, number][] {
  return globalTies
    .filter(([from, to]) => from >= tokenStart && to < tokenEnd)
    .map(([from, to]) => [from - tokenStart, to - tokenStart] as [number, number]);
}


export function PrintFigureLegend({
  items = [],
  entries,
}: {
  items?: string[];
  entries?: FigureLegendEntry[];
}) {
  if (entries?.length) {
    return <FigureLegendPanel entries={entries} printMode title="Figuras en esta obra" />;
  }
  if (!items.length) return null;
  return (
    <div className="print-figure-legend print-figure-legend--legacy">
      <p className="print-figure-legend__title">Figuras en esta obra</p>
      <ul className="print-figure-legend__list">
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

export function PrintRhythmCanvasBlock({ payload }: { payload: Record<string, unknown> }) {
  const prompt = humanizeMusicText(String(payload.prompt || 'Observa el patrón rítmico:'));
  const taskKind = String(payload.taskKind || 'complete');
  const matchPairs = Array.isArray(payload.matchPairs)
    ? (payload.matchPairs as Array<{ id?: string; token?: string; label?: string }>)
    : [];
  const tokens = normalizeTokenArray(
    (payload.originalBarTokens as string[] | undefined) ??
      (payload.patternTokens as string[] | undefined),
  ) as MusicalToken[];
  const method =
    payload.rhythmMethod === 'takadimi' || payload.rhythmMethod === 'kodaly'
      ? payload.rhythmMethod
      : 'tradicional';
  const beatCells = resolveBeatCells(payload, tokens);
  const useBeatCells = beatCells.length > 0 && !resolveScoreEvents(payload)?.length;
  const scoreEvents = resolveScoreEvents(payload);

  if (taskKind === 'match' && matchPairs.length) {
    const shuffled = [...matchPairs].sort((a, b) =>
      String(a.label).localeCompare(String(b.label), 'es'),
    );
    return (
      <div className="print-block__body">
        <p className="print-instruction">{prompt}</p>
        <div className="print-vincula-grid print-rhythm-match-grid">
          <div className="print-vincula-col print-vincula-col--a">
            <p className="print-vincula-head">Figuras</p>
            <ul className="print-vincula-list print-vincula-list--a">
              {matchPairs.map((pair, i) => (
                <li key={pair.id || i}>
                  <VexRhythmGlyph token={(pair.token || 'Q') as MusicalToken} size={32} printMode />
                  <span className="print-vincula-circle" aria-hidden>○</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="print-vincula-bridge" aria-hidden />
          <div className="print-vincula-col print-vincula-col--b">
            <p className="print-vincula-head">Nombres</p>
            <ul className="print-vincula-list print-vincula-list--b">
              {shuffled.map((pair, i) => (
                <li key={`r-${pair.id || i}`}>
                  <span className="print-vincula-circle" aria-hidden>○</span>
                  <span className="print-vincula-label">{String(pair.label || '')}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <p className="print-hint">Une con línea cada figura con su nombre en español.</p>
      </div>
    );
  }

  const usesWorkshop =
    taskKind === 'complete' ||
    taskKind === 'transcribe' ||
    taskKind === 'practice' ||
    taskKind === 'syllables' ||
    (taskKind === 'order' && useBeatCells);

  if (usesWorkshop && tokens.length > 0) {
    return (
      <div className="print-block__body">
        <p className="print-instruction">{prompt}</p>
        <PrintRhythmWorkshopSlots payload={payload} tokens={tokens} method={method} beatCells={beatCells} />
      </div>
    );
  }

  const cellOptions = Array.isArray(payload.cellOptions)
    ? (payload.cellOptions as Array<{ id?: string; label?: string; token?: string }>)
    : [];

  if (taskKind === 'identify' && (cellOptions.length || tokens.length)) {
    const options =
      cellOptions.length > 0
        ? cellOptions
        : (Array.isArray(payload.options) ? payload.options : ['Q', 'H', 'E', 'S']).map(
            (tok, i) => ({
              id: String(i),
              label: tokenToLabel(String(tok) as MusicalToken, method),
              token: String(tok),
            }),
          );
    return (
      <div className="print-block__body">
        <p className="print-instruction">{prompt}</p>
        <RhythmPatternStrip
          rhythmPattern={payload.rhythmPattern as never}
          patternTokens={tokens}
          beatCells={beatCells}
          scoreEvents={scoreEvents}
          timeSignature={payload.timeSignature as never}
          meter={payload.meter as string | undefined}
          targetBarNumber={Number(payload.targetBarNumber ?? payload.barIndex) || undefined}
          dottedIndices={
            Array.isArray(payload.rhythmDots)
              ? (payload.rhythmDots as number[])
              : Array.isArray(payload.dottedIndices)
                ? (payload.dottedIndices as number[])
                : []
          }
          rhythmMethod={payload.rhythmMethod as never}
          graphicMode={payload.graphicMode as never}
          slotMode="missing"
          printMode
        />
        <ul className="print-rhythm-mcq-list">
          {options.map((opt, i) => (
            <li key={opt.id || i} className="print-rhythm-mcq-item">
              <span className="print-vincula-circle" aria-hidden>
                ○
              </span>
              <VexRhythmGlyph token={(opt.token || 'Q') as MusicalToken} size={28} printMode />
              <span className="print-rhythm-mcq-label">{String(opt.label || '')}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="print-block__body">
      <p className="print-instruction">{prompt}</p>
      {usesWorkshop && tokens.length > 0 ? (
        <PrintRhythmWorkshopSlots payload={payload} tokens={tokens} method={method} beatCells={beatCells} />
      ) : (
        <RhythmPatternStrip
          rhythmPattern={payload.rhythmPattern as never}
          patternTokens={tokens}
          beatCells={beatCells}
          rhythmicCells={beatCells}
          timeSignature={payload.timeSignature as never}
          meter={payload.meter as string | undefined}
          targetBarNumber={Number(payload.targetBarNumber ?? payload.barIndex) || undefined}
          dottedIndices={
            Array.isArray(payload.rhythmDots)
              ? (payload.rhythmDots as number[])
              : Array.isArray(payload.dottedIndices)
                ? (payload.dottedIndices as number[])
                : []
          }
          rhythmMethod={payload.rhythmMethod as never}
          graphicMode={payload.graphicMode as never}
          slotMode="missing"
          printMode
        />
      )}
    </div>
  );
}

export function PrintMelodyStaffBlock({ payload }: { payload: Record<string, unknown> }) {
  const notes = Array.isArray(payload.notes)
    ? (payload.notes as Array<{ id?: string; pitch?: string; duration?: string }>)
    : [];
  const prompt = humanizeMusicText(String(payload.prompt || 'Identifica la nota señalada:'));

  return (
    <div className="print-block__body">
      <p className="print-instruction">{prompt}</p>
      <VexStaffFragment
        notes={notes}
        clef={payload.clef === 'bass' ? 'bass' : 'treble'}
        timeSignature={String(payload.timeSignature || '4/4')}
        highlightNoteId={String(payload.highlightNoteId || notes[0]?.id || '')}
        width={notes.length <= 4 ? 360 : 320}
        printMode
        density={
          payload.density === 'comfortable' || notes.length > 4 ? 'comfortable' : 'compact'
        }
        className="print-staff-wrap"
      />
      <p className="print-staff-answer-line">
        Nota: <span className="print-staff-blank" aria-hidden>________________</span>
      </p>
    </div>
  );
}

export function PrintSymbolAnalysisBlock({ payload }: { payload: Record<string, unknown> }) {
  const questionText = humanizeMusicText(String(payload.questionText || 'Observa el símbolo señalado:'));
  const modifiers = (payload.modifiers && typeof payload.modifiers === 'object'
    ? payload.modifiers
    : {}) as { ties?: [number, number][]; keySignature?: string; rhythmDots?: number[] };
  const highlight = payload.highlightedElement as
    | { type?: string; tokenIndex?: number; label?: string }
    | undefined;
  const options = Array.isArray(payload.options)
    ? (payload.options as Array<{ id?: string; text?: string }>)
    : [];
  const tokens = normalizeTokenArray(payload.patternTokens as string[] | undefined) as MusicalToken[];
  const phraseBars = Array.isArray(payload.phraseBarNumbers)
    ? (payload.phraseBarNumbers as number[])
    : undefined;
  const targetBar = Number(payload.targetBarNumber) || 1;
  const instruction =
    phraseBars && phraseBars.length > 1
      ? `Observa los compases ${phraseBars.join(' y ')} del repertorio:`
      : `Observa con atención el compás #${targetBar} del repertorio:`;

  return (
    <div className="print-block__body print-symbol-analysis">
      <p className="print-instruction">{instruction}</p>
      <SymbolAnalysisStaff
        patternTokens={tokens}
        timeSignature={(payload.timeSignature as never) ?? '4/4'}
        targetBarNumber={targetBar}
        modifiers={modifiers}
        scoreEvents={resolveScoreEvents(payload, targetBar)}
        scoreEventsByBar={payload.scoreEventsByBar as Record<number, ScoreRenderEvent[]> | undefined}
        phraseBarNumbers={phraseBars}
        barTokenStarts={
          Array.isArray(payload.barTokenStarts) ? (payload.barTokenStarts as number[]) : undefined
        }
        highlight={
          highlight?.label
            ? {
                type: (highlight.type as 'tie' | 'dot' | 'key_signature') || 'dot',
                tokenIndex: highlight.tokenIndex,
                label: highlight.label,
              }
            : undefined
        }
        printMode
        rhythmMethod={
          payload.rhythmMethod === 'takadimi' || payload.rhythmMethod === 'kodaly'
            ? payload.rhythmMethod
            : 'tradicional'
        }
      />
      {highlight?.label && (
        <p className="print-symbol-analysis__marker">
          Símbolo señalado: <strong>{highlight.label}</strong>
          {highlight.type === 'tie' ? ' (ligadura)' : highlight.type === 'dot' ? ' (puntillo)' : ' (armadura)'}
        </p>
      )}
      <p className="print-symbol-analysis__question">{questionText}</p>
      <ol className="print-symbol-analysis__options">
        {options.map((opt, i) => (
          <li key={opt.id || i}>
            <span className="print-symbol-analysis__checkbox" aria-hidden>
              ☐
            </span>{' '}
            {String(opt.text || '')}
          </li>
        ))}
      </ol>
    </div>
  );
}

export function PrintEarTrainingBlock({ payload }: { payload: Record<string, unknown> }) {
  const teacherScript = String(payload.teacherScript || 'Dictado docente: ejecute el fragmento en la sala.');
  const studentPrompt = humanizeMusicText(String(payload.studentPrompt || 'Escucha y responde:'));
  const kind = String(payload.kind || 'rhythm');
  const tokens = normalizeTokenArray(payload.patternTokens as string[] | undefined) as MusicalToken[];
  const method =
    payload.rhythmMethod === 'takadimi' || payload.rhythmMethod === 'kodaly'
      ? payload.rhythmMethod
      : 'tradicional';
  const beatCells = tokens.length ? resolveBeatCells(payload, tokens) : [];

  return (
    <div className="print-block__body">
      <div className="print-teacher-guide print-ear-training-guide">
        <p className="print-teacher-guide__label">🗣️ Dictado docente</p>
        <p className="print-teacher-guide__body">{teacherScript}</p>
      </div>
      <p className="print-instruction">{studentPrompt}</p>
      {kind === 'rhythm' && beatCells.length > 0 ? (
        <PrintBeatCellWorkshopSlots payload={{ ...payload, taskKind: 'transcribe', hidePattern: true }} beatCells={beatCells} method={method} />
      ) : kind === 'rhythm' && tokens.length > 0 ? (
        <PrintRhythmWorkshopSlots
          payload={{ ...payload, taskKind: 'transcribe', hidePattern: true, blankIndices: tokens.map((_, i) => i) }}
          tokens={tokens}
          method={method}
        />
      ) : (
        <div className="print-writing-lines" aria-hidden>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="print-writing-line" />
          ))}
        </div>
      )}
    </div>
  );
}

type AnatomiaPrintItem = {
  id?: string;
  token?: string;
  symbol?: string;
  figureLabel?: string;
  correctBeats?: number;
  beatIndex?: number;
  takadimi?: string;
  cellId?: string;
};

export function PrintAnatomiaBlock({ payload }: { payload: Record<string, unknown> }) {
  const meter = String(payload.meter || payload.timeSignature || '4/4');
  const targetBar = Number(payload.targetBarNumber) || undefined;
  const method =
    payload.rhythmMethod === 'takadimi' || payload.rhythmMethod === 'kodaly'
      ? payload.rhythmMethod
      : 'takadimi';
  const tokens = normalizeTokenArray(
    (payload.patternTokens as string[] | undefined) ??
      (payload.items as AnatomiaPrintItem[] | undefined)?.map((item) => item.token).filter(Boolean),
  ) as MusicalToken[];
  const beatCells = resolveBeatCells(payload, tokens);
  const items = Array.isArray(payload.items) ? (payload.items as AnatomiaPrintItem[]) : [];

  return (
    <div className="print-block__body print-anatomia-block">
      <p className="print-instruction">
        Compás {meter}
        {targetBar ? ` · tiempo ${targetBar}` : ''} — escribe cuántos tiempos vale cada figura:
      </p>

      {tokens.length > 0 && (
        <div className="print-anatomia-block__context">
          <RhythmPatternStrip
            patternTokens={tokens}
            timeSignature={meter as never}
            rhythmMethod={method as never}
            targetBarNumber={targetBar}
            showSlots={false}
            printMode
          />
        </div>
      )}

      {beatCells.length > 0 && (
        <div className="print-anatomia-block__beats" aria-hidden>
          <BeatCellStrip beatCells={beatCells} timeSignature={meter as never} targetBarNumber={targetBar} />
        </div>
      )}

      <ul className="print-anatomia-list print-anatomia-list--vex">
        {items.map((item, i) => {
          const token = (item.token || 'Q') as MusicalToken;
          const beatLabel = item.beatIndex ? `Tiempo ${item.beatIndex}` : `Figura ${i + 1}`;
          return (
            <li key={item.id || i} className="print-anatomia-row">
              <span className="print-anatomia-beat">{beatLabel}</span>
              <span className="print-anatomia-glyph">
                <VexRhythmGlyph token={token} size={44} printMode />
              </span>
              <span className="print-anatomia-meta">
                <strong>{String(item.figureLabel || tokenToLabel(token, method))}</strong>
                {item.takadimi ? (
                  <span className="print-anatomia-takadimi">{item.takadimi}</span>
                ) : null}
              </span>
              <span className="print-anatomia-eq">=</span>
              <span className="print-anatomia-blank" aria-hidden />
              <span className="print-anatomia-unit">tiempo(s)</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function PrintDictadoRhythmBlock({ payload }: { payload: Record<string, unknown> }) {
  const options = Array.isArray(payload.options)
    ? (payload.options as unknown[]).map(String).filter(Boolean)
    : [];
  const cellOptions = Array.isArray(payload.cellOptions)
    ? (payload.cellOptions as Array<{ id?: string; label?: string }>)
    : [];
  const isMcq = options.length >= 2 || cellOptions.length >= 2;

  const studentPrompt = humanizeMusicText(
    String(
      payload.studentPrompt ||
        payload.prompt ||
        (isMcq
          ? 'Elige la opción que coincide con el patrón dictado.'
          : 'Transcribe el patrón rítmico en la cuadrícula:'),
    ),
  );
  const teacherScript = String(payload.teacherScript || '');
  const tokens = normalizeTokenArray(payload.patternTokens as string[] | undefined) as MusicalToken[];
  const method =
    payload.rhythmMethod === 'takadimi' || payload.rhythmMethod === 'kodaly'
      ? payload.rhythmMethod
      : 'takadimi';
  const meter = String(payload.meter || payload.timeSignature || '4/4');
  const beatCells = tokens.length ? resolveBeatCells(payload, tokens) : [];
  const lines = Number(payload.transcriptionLines) || 4;

  return (
    <div className="print-block__body print-dictado-rhythm">
      {teacherScript && (
        <div className="print-teacher-guide">
          <p className="print-teacher-guide__label">🗣️ Dictado docente</p>
          <p className="print-teacher-guide__body">{teacherScript}</p>
        </div>
      )}
      {/* Una sola instrucción al alumno: MCQ o transcripción — no ambas. */}
      <p className="print-instruction">{studentPrompt}</p>
      {isMcq ? (
        <ul className="print-rhythm-mcq-list">
          {(cellOptions.length
            ? cellOptions.map((o) => String(o.label || ''))
            : options
          ).map((label, i) => (
            <li key={i} className="print-rhythm-mcq-item">
              <span className="print-vincula-circle" aria-hidden>
                ○
              </span>
              <span>{label}</span>
            </li>
          ))}
        </ul>
      ) : beatCells.length > 0 ? (
        <PrintBeatCellWorkshopSlots
          payload={{ ...payload, taskKind: 'transcribe', hidePattern: true, meter }}
          beatCells={beatCells}
          method={method}
        />
      ) : (
        <div className="print-dictado-rhythm__grid" aria-hidden>
          {Array.from({ length: Math.max(lines, 4) }).map((_, i) => (
            <div key={i} className="print-dictado-rhythm__cell">
              <span className="print-dictado-rhythm__num">{i + 1}</span>
              <div className="print-dictado-rhythm__blank" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function PrintVexRhythmMeasureBlock({
  payload,
  barCount = 1,
}: {
  payload: Record<string, unknown>;
  barCount?: number;
}) {
  const tokens = normalizeTokenArray(payload.patternTokens as string[] | undefined) as MusicalToken[];
  const meter = String(payload.timeSignature || payload.meter || '4/4');
  const method =
    payload.rhythmMethod === 'takadimi' || payload.rhythmMethod === 'kodaly'
      ? payload.rhythmMethod
      : 'takadimi';
  const phraseBars = Array.isArray(payload.phraseBarNumbers)
    ? (payload.phraseBarNumbers as number[])
    : null;
  const keySignature = (payload.modifiers as { keySignature?: string } | undefined)?.keySignature;

  if (phraseBars && phraseBars.length > 1) {
    const phraseBarsData = buildPhraseScoreBars({
      phraseBarNumbers: phraseBars,
      barTokenStarts: Array.isArray(payload.barTokenStarts)
        ? (payload.barTokenStarts as number[])
        : undefined,
      patternTokens: tokens,
      rhythmDots: Array.isArray(payload.rhythmDots) ? (payload.rhythmDots as number[]) : [],
      scoreEventsByBar: payload.scoreEventsByBar as Record<number, ScoreRenderEvent[]> | undefined,
      modifiers: payload.modifiers as { ties?: [number, number][] } | undefined,
    });

    return (
      <div className="print-rhythm-bar-block print-rhythm-bar-block--phrase">
        <VexPhraseScore
          bars={phraseBarsData}
          timeSignature={meter as never}
          keySignature={keySignature}
          printMode
          caption={phraseScoreCaption(phraseBars, meter as never)}
        />
      </div>
    );
  }

  const beatCells = tokens.length ? resolveBeatCells(payload, tokens) : [];
  const barNumbers = phraseBars ?? Array.from({ length: Math.max(1, Math.min(barCount, 4)) }, (_, i) => i + 1);
  const barTokenStarts = Array.isArray(payload.barTokenStarts)
    ? (payload.barTokenStarts as number[])
    : null;

  const globalDots = Array.isArray(payload.rhythmDots)
    ? (payload.rhythmDots as number[])
    : Array.isArray(payload.dottedIndices)
      ? (payload.dottedIndices as number[])
      : [];
  const globalTies = Array.isArray((payload.modifiers as { ties?: [number, number][] } | undefined)?.ties)
    ? ((payload.modifiers as { ties: [number, number][] }).ties ?? [])
    : [];

  return (
    <div className="print-rhythm-bar-block">
      {barNumbers.map((barNum, barIdx) => {
        const tokenStart = barTokenStarts?.[barIdx] ?? 0;
        const tokenEnd = barTokenStarts?.[barIdx + 1] ?? tokens.length;
        const barTokens = phraseBars?.length ? tokens.slice(tokenStart, tokenEnd) : tokens;
        const barScoreEvents = resolveScoreEvents(payload, barNum);
        const barBeatCells =
          phraseBars?.length && !barScoreEvents?.length
            ? resolveBeatCells({ ...payload, patternTokens: barTokens }, barTokens)
            : beatCells;
        const barDots = phraseBars?.length ? perBarSliceIndices(globalDots, tokenStart, tokenEnd) : globalDots;
        const barTies = phraseBars?.length ? perBarTiePairs(globalTies, tokenStart, tokenEnd) : globalTies;
        return (
          <div key={barIdx} className="print-rhythm-bar-block__measure">
            <RhythmPatternStrip
              patternTokens={barTokens}
              beatCells={barBeatCells}
              rhythmicCells={barBeatCells}
              scoreEvents={barScoreEvents}
              timeSignature={meter as never}
              rhythmMethod={method as never}
              graphicMode={payload.graphicMode as never}
              dottedIndices={barDots}
              ties={barTies}
              keySignature={keySignature}
              targetBarNumber={barNum}
              showSlots={false}
              printMode
            />
          </div>
        );
      })}
    </div>
  );
}
